// Fichier: src/js/components/ScrollEffects.js
// Système d'effets de scroll avancés intégré au portfolio

import { effectsConfig } from '../config/effects-config.js';
import { utils } from '../core/utils.js';

export class ScrollEffects {
  constructor() {
    this.config = effectsConfig.scrollEffects || this.getDefaultConfig();
    this.elements = new Map();
    this.countersAnimated = new Set();
    this.isScrolling = false;
    this.lastScrollY = 0;
    this.scrollDirection = 'down';
    
    this.init();
  }

  getDefaultConfig() {
    return {
      parallax: {
        enabled: true,
        layers: [0.2, 0.4, 0.6, 0.8],
        throttle: 16
      },
      reveal: {
        enabled: true,
        offset: 100,
        stagger: 100,
        threshold: 0.1
      },
      counters: {
        enabled: true,
        duration: 2000,
        easing: 'easeOutQuart'
      },
      progress: {
        enabled: true,
        selector: '.scroll-progress'
      },
      typewriter: {
        enabled: true,
        speed: 50,
        delay: 1000
      }
    };
  }

  init() {
    this.createScrollProgress();
    this.setupParallaxElements();
    this.setupRevealElements();
    this.setupCounters();
    this.setupTypewriterElements();
    this.bindEvents();
    this.initializeVisibleElements();
    
    console.log('🚀 ScrollEffects initialisé');
  }

  // ===== BARRE DE PROGRESSION =====
  createScrollProgress() {
    if (!this.config.progress.enabled) return;
    
    let progressBar = document.querySelector('.scroll-progress');
    if (!progressBar) {
      progressBar = document.createElement('div');
      progressBar.className = 'scroll-progress';
      progressBar.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 0%;
        height: 4px;
        background: linear-gradient(90deg, var(--primary), var(--primary-400));
        z-index: 1000;
        transition: width 0.1s ease;
      `;
      document.body.appendChild(progressBar);
    }
    this.progressBar = progressBar;
  }

  updateScrollProgress() {
    if (!this.progressBar) return;
    
    const scrollTop = window.pageYOffset;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.min((scrollTop / docHeight) * 100, 100);
    
    this.progressBar.style.width = scrollPercent + '%';
  }

  // ===== EFFETS DE PARALLAXE =====
  setupParallaxElements() {
    if (!this.config.parallax.enabled) return;
    
    // Créer les couches de parallaxe automatiquement
    const sections = [];
    
    sections.forEach(selector => {
      const section = document.querySelector(selector);
      if (!section) return;
      
      // Ajouter les couches de parallaxe s'il n'y en a pas
      if (!section.querySelector('.parallax-layer')) {
        this.createParallaxLayers(section);
      }
    });

    // Enregistrer tous les éléments parallax
    const parallaxElements = document.querySelectorAll('[data-parallax-speed], .parallax-layer');
    parallaxElements.forEach(element => {
      const speed = parseFloat(element.dataset.parallaxSpeed) || 0.5;
      this.elements.set(element, { type: 'parallax', speed });
    });
  }

  createParallaxLayers(section) {
    // Créer le conteneur parallax
    const parallaxContainer = document.createElement('div');
    parallaxContainer.className = 'parallax-container';
    parallaxContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      pointer-events: none;
      z-index: -1;
    `;

    // Créer les couches
    this.config.parallax.layers.forEach((speed, index) => {
      const layer = document.createElement('div');
      layer.className = `parallax-layer parallax-layer-${index}`;
      layer.dataset.parallaxSpeed = speed;
      layer.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 120%;
        will-change: transform;
      `;

      // Styles spécifiques par couche
      if (index === 0) {
        layer.style.background = 'linear-gradient(135deg, var(--primary) 0%, #8b5cf6 50%, #ec4899 100%)';
        layer.style.opacity = '0.05';
      } else if (index === 1) {
        layer.style.backgroundImage = `
          radial-gradient(circle at 20% 20%, var(--primary-50) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)
        `;
      }

      parallaxContainer.appendChild(layer);
    });

    // Insérer dans la section
    section.style.position = 'relative';
    section.appendChild(parallaxContainer);
  }

  updateParallax() {
    if (!this.config.parallax.enabled) return;
    
    const scrolled = window.pageYOffset;
    
    this.elements.forEach((data, element) => {
      if (data.type === 'parallax') {
        const yPos = -(scrolled * data.speed);
        element.style.transform = `translateY(${yPos}px)`;
      }
    });
  }

  // ===== ANIMATIONS DE RÉVÉLATION =====
  setupRevealElements() {
    if (!this.config.reveal.enabled) return;
    
    // Ajouter les classes CSS si elles n'existent pas
    this.injectRevealStyles();
    
    // Configurer l'Intersection Observer
    this.setupIntersectionObserver();
    
    // Enregistrer les éléments à révéler
    const revealSelectors = [
      '.scroll-reveal',
      '.scroll-reveal-left', 
      '.scroll-reveal-right',
      '.scroll-reveal-scale',
      '.animate-on-scroll',
      '.project-card',
      '.skill-item',
      '.timeline-item'
    ];
    
    revealSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(element => {
        this.elements.set(element, { type: 'reveal' });
        element.classList.add('scroll-reveal-hidden');
      });
    });
  }

  injectRevealStyles() {
    const styleId = 'scroll-reveal-styles';
    if (document.getElementById(styleId)) return;
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .scroll-reveal-hidden {
        opacity: 0;
        transform: translateY(50px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
      }
      
      .scroll-reveal-left.scroll-reveal-hidden {
        transform: translateX(-50px);
      }
      
      .scroll-reveal-right.scroll-reveal-hidden {
        transform: translateX(50px);
      }
      
      .scroll-reveal-scale.scroll-reveal-hidden {
        transform: scale(0.8);
      }
      
      .scroll-reveal-visible {
        opacity: 1;
        transform: translateY(0) translateX(0) scale(1);
      }
      
      /* Stagger delays */
      .scroll-reveal-visible:nth-child(1) { transition-delay: 0ms; }
      .scroll-reveal-visible:nth-child(2) { transition-delay: 100ms; }
      .scroll-reveal-visible:nth-child(3) { transition-delay: 200ms; }
      .scroll-reveal-visible:nth-child(4) { transition-delay: 300ms; }
      .scroll-reveal-visible:nth-child(5) { transition-delay: 400ms; }
    `;
    document.head.appendChild(style);
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          
          // Ajouter délai basé sur la position dans le conteneur parent
          const siblings = Array.from(element.parentElement.children);
          const index = siblings.indexOf(element);
          const delay = index * this.config.reveal.stagger;
          
          setTimeout(() => {
            element.classList.remove('scroll-reveal-hidden');
            element.classList.add('scroll-reveal-visible');
          }, delay);
          
          this.observer.unobserve(element);
        }
      });
    }, {
      threshold: this.config.reveal.threshold,
      rootMargin: `0px 0px -${this.config.reveal.offset}px 0px`
    });
  }

  // ===== COMPTEURS ANIMÉS =====
  setupCounters() {
    if (!this.config.counters.enabled) return;
    
    const counters = document.querySelectorAll('[data-counter-target], .counter[data-target]');
    counters.forEach(counter => {
      const target = parseInt(counter.dataset.counterTarget || counter.dataset.target);
      if (!isNaN(target)) {
        this.elements.set(counter, { type: 'counter', target });
      }
    });
  }

  animateCounter(element, target) {
    if (this.countersAnimated.has(element)) return;
    
    this.countersAnimated.add(element);
    
    const duration = this.config.counters.duration;
    const startTime = Date.now();
    const startValue = 0;
    
    const updateCounter = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (easeInOutCubic for a smoother effect)
      const easeInOutCubic = progress < 0.5 
        ? 4 * progress * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      const current = Math.floor(target * easeInOutCubic);
      
      element.textContent = this.formatNumber(current);
      
      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        element.textContent = this.formatNumber(target);
      }
    };
    
    updateCounter();
  }

  formatNumber(num) {
    return num.toLocaleString();
  }

  // ===== EFFET TYPEWRITER =====
  setupTypewriterElements() {
    if (!this.config.typewriter.enabled) return;
    
    const typewriterElements = document.querySelectorAll('.typewriter, [data-typewriter]');
    typewriterElements.forEach(element => {
      const text = element.dataset.typewriter || element.textContent;
      this.elements.set(element, { type: 'typewriter', text });
    });
  }

  animateTypewriter(element, text) {
    element.style.borderRight = '2px solid var(--primary)';
    element.style.whiteSpace = 'nowrap';
    element.style.overflow = 'hidden';
    element.textContent = '';
    
    let i = 0;
    const typeChar = () => {
      if (i < text.length) {
        element.textContent += text.charAt(i);
        i++;
        setTimeout(typeChar, this.config.typewriter.speed);
      } else {
        setTimeout(() => {
          element.style.borderRight = 'none';
        }, 500);
      }
    };
    
    setTimeout(typeChar, this.config.typewriter.delay);
  }

  // ===== GESTION DES ÉVÉNEMENTS =====
  bindEvents() {
    // Throttled scroll handler
    const scrollHandler = utils.throttle(() => {
      this.handleScroll();
    }, this.config.parallax.throttle);
    
    window.addEventListener('scroll', scrollHandler, { passive: true });
    
    // Resize handler
    const resizeHandler = utils.debounce(() => {
      this.handleResize();
    }, 250);
    
    window.addEventListener('resize', resizeHandler);
    
    // Intersection observer pour les compteurs
    this.setupCounterObserver();
  }

  handleScroll() {
    const currentScrollY = window.pageYOffset;
    this.scrollDirection = currentScrollY > this.lastScrollY ? 'down' : 'up';
    this.lastScrollY = currentScrollY;
    
    this.updateScrollProgress();
    this.updateParallax();
  }

  handleResize() {
    // Recalculer les positions si nécessaire
    this.elements.forEach((data, element) => {
      if (data.type === 'reveal' && element.classList.contains('scroll-reveal-visible')) {
        // Forcer la mise à jour des animations
        element.style.transform = 'none';
        element.offsetHeight; // Force reflow
        element.style.transform = '';
      }
    });
  }

  setupCounterObserver() {
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const data = this.elements.get(element);
          
          if (data && data.type === 'counter') {
            this.animateCounter(element, data.target);
          }
          
          counterObserver.unobserve(element);
        }
      });
    }, {
      threshold: 0.5
    });
    
    this.elements.forEach((data, element) => {
      if (data.type === 'counter') {
        counterObserver.observe(element);
      }
    });
  }

  // ===== INITIALISATION =====
  initializeVisibleElements() {
    // Observer tous les éléments de révélation
    this.elements.forEach((data, element) => {
      if (data.type === 'reveal') {
        this.observer.observe(element);
      }
    });
    
    // Démarrer les typewriters visibles
    setTimeout(() => {
      this.elements.forEach((data, element) => {
        if (data.type === 'typewriter') {
          const rect = element.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            this.animateTypewriter(element, data.text);
          }
        }
      });
    }, 100);
  }

  // ===== API PUBLIQUE =====
  
  // Méthode pour révéler manuellement un élément
  revealElement(element) {
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }
    
    if (element) {
      element.classList.remove('scroll-reveal-hidden');
      element.classList.add('scroll-reveal-visible');
    }
  }
  
  // Méthode pour cacher un élément
  hideElement(element) {
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }
    
    if (element) {
      element.classList.remove('scroll-reveal-visible');
      element.classList.add('scroll-reveal-hidden');
    }
  }
  
  // Activer/désactiver les effets
  toggle(effectType, enabled) {
    if (this.config[effectType]) {
      this.config[effectType].enabled = enabled;
    }
  }
  
  // Nettoyer les event listeners
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Nettoyer les event listeners
    // Note: Il faudrait garder les références pour pouvoir les supprimer
    console.log('ScrollEffects détruit');
  }
}

// Export par défaut
export default ScrollEffects;